import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { createQuarantineStore, type QuarantineStoreApi } from './createStore';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import type { QuarantineStore } from './types';

const QuarantineContext = createContext<QuarantineStoreApi | null>(null);

export const QuarantineProvider = ({
  children,
  ...props
}: {
  children: ReactNode;
  orgId: string;
  utcOffset: string;
}) => {
  const storeRef = useRef<QuarantineStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createQuarantineStore(props);
  }
  useEffect(() => {
    const state = storeRef.current?.getState();
    if (!state) return;
    // Here we avoid recreating the store when the props change
    const diff: Record<string, string | undefined> = {
      orgId: undefined,
      utcOffset: undefined,
    };
    if (state.currentOrgId !== props.orgId) {
      diff.orgId = props.orgId;
      state.setOrgId(props.orgId);
    }
    if (state.currentUtcOffset !== props.utcOffset) {
      diff.utcOffset = props.utcOffset;
      state.setUtcOffset(props.utcOffset);
    }
    state.riskAi.onPropsChange(diff);
  }, [props.orgId, props.utcOffset]);
  return (
    <QuarantineContext.Provider value={storeRef.current}>
      {children}
    </QuarantineContext.Provider>
  );
};

export const useQuarantineStore = <T,>(
  selector: (state: QuarantineStore) => T,
  equalityFn?: (left: T, right: T) => boolean
): T => {
  const store = useContext(QuarantineContext);
  if (!store) {
    throw new Error('Missing QuarantineContext.Provider in the tree');
  }
  return useStoreWithEqualityFn(store, selector, equalityFn);
};
